import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField,
  MenuItem, Alert, CircularProgress, InputAdornment, IconButton, Typography,
} from '@mui/material';
import { ShoppingCart, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { adminRecordPurchase } from 'api/wallet';
import EvidenceUploadButton from 'components/EvidenceUploadButton';
import { sectionLabelSx, fieldSx } from 'components/bento/BentoFormControls';

const CURRENCIES = ['THB', 'LAK', 'USD'];
const CURRENCY_SYMBOLS = { THB: '฿', LAK: '₭', USD: '$' };
const PLATFORMS = ['Lazada', 'Shopee', 'Tiktok', 'Shein', 'Other'];
const todayIso = () => new Date().toISOString().slice(0, 10);
const EMPTY_FORM = { amount: '', currency: 'THB', description: '', platform: '', platformOther: '', orderDate: todayIso() };

// Purchase order dialog for a customer whose wallet account is already known
// (reached from the account transactions screen) — mirrors WalletTopupDialog.
// Purchase orders are online-shop purchases (Lazada/Shopee/Tiktok/Shein/Other)
// made on the customer's behalf — they aren't tied to any branch/station.
const WalletPurchaseOrderDialog = ({ open, onClose, account, onSuccess }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setFile(null);
    setError('');
  }, [open, account]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!account || !form.amount || !form.platform) {
      setError(t('wallet.admin.purchasePlatformRequired'));
      return;
    }
    if (form.platform === 'Other' && !form.platformOther.trim()) {
      setError(t('wallet.admin.purchasePlatformOtherRequired'));
      return;
    }
    if (!file) {
      setError(t('wallet.admin.evidenceRequired'));
      return;
    }
    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      fd.append('amount', form.amount);
      fd.append('currency', form.currency);
      fd.append('description', form.description);
      fd.append('platform', form.platform);
      if (form.platform === 'Other') fd.append('platformOther', form.platformOther.trim());
      if (form.orderDate) fd.append('orderDate', form.orderDate);
      fd.append('evidencePhoto', file);
      await adminRecordPurchase(account.id, fd);
      onSuccess?.({ amount: parseFloat(form.amount), currency: form.currency });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontWeight: 800 }}>
        <Box>
          {t('wallet.admin.purchaseOrderTab')}
          {account && (
            <Typography variant="caption" color="text.secondary" display="block">
              {account.id} — {account.customer?.name}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={loading}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>{t('wallet.admin.purchaseOrderNote')}</Alert>

        <Box sx={sectionLabelSx}>{t('wallet.admin.sections.purchaseDetails')}</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr' }, gap: '16px', mb: '16px' }}>
          <TextField
            label={t('wallet.amount')}
            type="number"
            value={form.amount}
            onChange={set('amount')}
            inputProps={{ min: 0.01, step: 0.01 }}
            sx={fieldSx}
            InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOLS[form.currency]}</InputAdornment> }}
          />
          <TextField select label={t('wallet.currency')} value={form.currency} onChange={set('currency')} sx={fieldSx}>
            {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: form.platform === 'Other' ? '1fr 1fr' : '1fr' }, gap: '16px', mb: '16px' }}>
          <TextField
            select
            required
            fullWidth
            label={t('wallet.admin.platform')}
            value={form.platform}
            onChange={set('platform')}
            sx={fieldSx}
          >
            {PLATFORMS.map((p) => (
              <MenuItem key={p} value={p}>{t(`wallet.admin.platforms.${p}`)}</MenuItem>
            ))}
          </TextField>
          {form.platform === 'Other' && (
            <TextField
              fullWidth
              required
              label={t('wallet.admin.platformOther')}
              value={form.platformOther}
              onChange={set('platformOther')}
              sx={fieldSx}
            />
          )}
        </Box>

        <TextField
          fullWidth
          type="date"
          label={t('wallet.admin.orderDate')}
          value={form.orderDate}
          onChange={set('orderDate')}
          InputLabelProps={{ shrink: true }}
          sx={{ ...fieldSx, mb: '16px' }}
        />

        <TextField
          fullWidth
          label={t('wallet.description')}
          value={form.description}
          onChange={set('description')}
          placeholder="Optional extra details..."
          sx={{ ...fieldSx, mb: '16px' }}
        />

        <Box sx={sectionLabelSx}>{t('wallet.admin.sections.evidence')}</Box>
        <EvidenceUploadButton
          file={file}
          onChange={(e) => setFile(e.target.files[0] || null)}
          label={t('wallet.admin.evidenceRequired')}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={loading || !form.amount || !form.platform || (form.platform === 'Other' && !form.platformOther.trim()) || !file}
          startIcon={loading ? <CircularProgress size={18} /> : <ShoppingCart fontSize="small" />}
        >
          {loading ? t('wallet.admin.recordingPurchase') : t('wallet.admin.purchaseOrderTab')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletPurchaseOrderDialog;
